import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Form, 
  Input, 
  Button, 
  Table, 
  Space, 
  Tag, 
  Modal, 
  Image,
  Drawer,
  Descriptions,
  message,
  Spin,
  Select,
  DatePicker,
  Row,
  Col
} from 'antd';
import { 
  SearchOutlined, 
  EyeOutlined, 
  EditOutlined,
  ShareAltOutlined,
  DownloadOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { queryShipments, getShipment, createShareableLink, getShareableLinks, deleteShareableLink } from '../services/shipmentService';
import { formatDateTime, formatDate } from '../utils/dateUtils';
import { getConfig } from '../services/api';
import locale from 'antd/es/date-picker/locale/zh_CN';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Query = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentShipment, setCurrentShipment] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [existingLinks, setExistingLinks] = useState([]);
  const [showCreateLinkForm, setShowCreateLinkForm] = useState(false);
  const [expirationDays, setExpirationDays] = useState(7); // 默认7天有效期
  
  // 获取API基础URL
  const baseUrl = getConfig().workerUrl || 'https://warehouseapi.proxypool.eu.org';
  
  // 修复图片URL函数
  const fixImageUrl = (url) => {
    if (!url) return '';
    
    // 如果不是字符串，转换为字符串
    if (typeof url !== 'string') {
      console.warn('非字符串URL:', url);
      try {
        url = String(url);
      } catch (e) {
        console.error('转换URL失败:', e);
        return '';
      }
    }
    
    // 如果是 localhost 的URL，替换为图片服务器URL
    if (url.includes('localhost') || url.includes('127.0.0.1')) {
      // 提取文件名部分
      const fileName = url.split('/').pop().split('?')[0];
      console.log('从 localhost URL 提取文件名:', fileName);
      const imgServerUrl = `https://imgapi.proxypool.eu.org/${fileName}`;
      console.log('替换为图片服务器URL:', imgServerUrl);
      return imgServerUrl;
    }
    
    // 如果URL已经是完整的URL，直接返回
    if (url.startsWith('http')) {
      console.log('使用完整URL:', url);
      return url;
    }
    
    // 如果是相对路径，添加图片服务器基础URL
    if (url.startsWith('/') || !url.includes('/')) {
      // 如果是纯文件名或相对路径，使用图片服务器URL
      const fileName = url.startsWith('/') ? url.substring(1) : url;
      const imgServerUrl = `https://imgapi.proxypool.eu.org/${fileName}`;
      console.log('添加图片服务器URL:', imgServerUrl);
      return imgServerUrl;
    }
    
    // 否则返回原始URL
    console.log('使用原始URL:', url);
    return url;
  };
  
  // 查询运单
  // 添加调试状态
  const [debugInfo, setDebugInfo] = useState(null);
  
  const handleSearch = async (values) => {
    setLoading(true);
    setDebugInfo(null); // 清除之前的调试信息
    
    try {
      // 处理日期范围
      let params = { ...values };
      
      if (values.dateRange && values.dateRange.length === 2) {
        params.startDate = values.dateRange[0].format('YYYY-MM-DD');
        params.endDate = values.dateRange[1].format('YYYY-MM-DD');
        delete params.dateRange;
      }
      
      // 添加分页参数
      params.page = pagination.current;
      params.pageSize = pagination.pageSize;
      
      console.log('发送查询参数:', params);
      const data = await queryShipments(params);
      console.log('查询响应:', data);
      
      // 保存原始响应以便调试
      if (data.originalResponse) {
        setDebugInfo(data.originalResponse);
      }
      
      setShipments(data.items || []);
      setPagination({
        ...pagination,
        total: data.total || 0
      });
    } catch (error) {
      console.error('查询错误:', error);
      message.error(error.message || '查询失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 处理表格分页变化
  const handleTableChange = (pagination) => {
    setPagination(pagination);
    form.submit(); // 重新查询
  };
  
  // 查看运单详情
  const showDetail = async (id) => {
    setDetailLoading(true);
    setDetailVisible(true);
    
    try {
      console.log('开始获取运单详情, ID:', id);
      const shipment = await getShipment(id);
      console.log('原始运单详情:', shipment);
      
      // 如果没有运单数据，显示错误
      if (!shipment) {
        throw new Error('未找到运单数据');
      }
      
      // 将响应数据显示在调试信息中
      setDebugInfo({
        shipmentData: shipment,
        imageUrls: shipment.image_urls
      });
      
      // 处理图片URL
      const processedShipment = {
        ...shipment,
        // 确保运单号字段存在
        tracking: shipment.tracking_no || shipment.tracking || '',
        // 初始化图片数组
        images: []
      };
      
      console.log('处理前的图片URLs类型:', typeof shipment.image_urls);
      
      // 如果有图片URL字符串，尝试解析为JSON
      if (typeof shipment.image_urls === 'string' && shipment.image_urls) {
        try {
          processedShipment.image_urls = JSON.parse(shipment.image_urls);
          console.log('解析字符串后的图片URLs:', processedShipment.image_urls);
        } catch (e) {
          console.error('解析图片URL字符串失败:', e);
          processedShipment.image_urls = { cargo: [], packing: [], shipping: [] };
        }
      } else if (!shipment.image_urls || typeof shipment.image_urls !== 'object') {
        // 如果图片URL不存在或不是对象，初始化为空对象
        console.log('图片URLs不存在或格式不正确，初始化为空对象');
        processedShipment.image_urls = { cargo: [], packing: [], shipping: [] };
      }
      
      // 如果有raw_image_urls，显示在调试信息中
      if (shipment.raw_image_urls) {
        setDebugInfo(prev => ({
          ...prev,
          raw_image_urls: shipment.raw_image_urls
        }));
      }
      
      // 确保 image_urls 字段是对象类型
      if (processedShipment.image_urls && typeof processedShipment.image_urls === 'object') {
        console.log('处理图片URLs:', processedShipment.image_urls);
        
        // 处理货物图片
        if (processedShipment.image_urls.cargo && Array.isArray(processedShipment.image_urls.cargo)) {
          console.log('添加货物图片:', processedShipment.image_urls.cargo.length, '张');
          processedShipment.images.push(...processedShipment.image_urls.cargo);
        }
        
        // 处理包装图片
        if (processedShipment.image_urls.packing && Array.isArray(processedShipment.image_urls.packing)) {
          console.log('添加包装图片:', processedShipment.image_urls.packing.length, '张');
          processedShipment.images.push(...processedShipment.image_urls.packing);
        }
        
        // 处理运输图片
        if (processedShipment.image_urls.shipping && Array.isArray(processedShipment.image_urls.shipping)) {
          console.log('添加运输图片:', processedShipment.image_urls.shipping.length, '张');
          processedShipment.images.push(...processedShipment.image_urls.shipping);
        }
        
        console.log('处理后的图片总数:', processedShipment.images.length);
      } else {
        console.warn('图片URLs不是有效的对象:', processedShipment.image_urls);
      }
      
      console.log('处理后的运单详情:', processedShipment);
      setCurrentShipment(processedShipment);
    } catch (error) {
      console.error('获取运单详情失败:', error);
      message.error(error.message || '获取运单详情失败');
      setDetailVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };
  
  // 关闭详情抽屉
  const closeDetail = () => {
    setDetailVisible(false);
    setCurrentShipment(null);
  };
  
  // 获取现有分享链接
  const fetchExistingLinks = async (shipmentId) => {
    console.log('获取现有分享链接，运单ID:', shipmentId);
    setShareLinkLoading(true);
    
    try {
      // 确保 ID 是有效的
      if (!shipmentId) {
        console.error('无效的运单ID');
        throw new Error('无效的运单ID');
      }
      
      // 获取现有链接
      const links = await getShareableLinks(shipmentId);
      console.log('获取到的现有链接:', links);
      
      // 设置现有链接列表
      setExistingLinks(links || []);
      
      // 如果有现有链接，显示第一个链接
      if (links && links.length > 0) {
        const activeLink = links.find(link => !link.isExpired) || links[0];
        const shareUrl = `${window.location.origin}/share/${activeLink.id}`;
        setShareLink(shareUrl);
      } else {
        setShareLink('');
        setShowCreateLinkForm(true); // 没有现有链接，显示创建表单
      }
    } catch (error) {
      console.error('获取分享链接错误:', error);
      message.error(error.message || '获取分享链接失败');
      setShareLink('');
      setShowCreateLinkForm(true); // 出错时显示创建表单
    } finally {
      setShareLinkLoading(false);
    }
  };
  
  // 处理删除分享链接
  const handleDeleteLink = async (linkId) => {
    try {
      setShareLinkLoading(true);
      console.log('删除分享链接:', linkId);
      
      // 确认删除
      Modal.confirm({
        title: '确认删除',
        content: '您确定要删除这个分享链接吗？删除后无法恢复。',
        okText: '确认删除',
        okType: 'danger',
        cancelText: '取消',
        onOk: async () => {
          await deleteShareableLink(linkId);
          message.success('分享链接删除成功');
          
          // 重新获取分享链接列表
          if (currentShipment && currentShipment.id) {
            await fetchExistingLinks(currentShipment.id);
          }
        },
      });
    } catch (error) {
      console.error('删除分享链接错误:', error);
      message.error(`删除分享链接失败: ${error.message}`);
    } finally {
      setShareLinkLoading(false);
    }
  };
  
  // 处理分享按钮点击
  const handleShare = async (id) => {
    console.log('处理分享按钮点击，运单ID:', id);
    setShareModalVisible(true);
    setShareLinkLoading(true);
    setShowCreateLinkForm(false);
    
    try {
      // 确保 ID 是有效的
      if (!id) {
        console.error('无效的运单ID');
        throw new Error('无效的运单ID');
      }
      
      // 如果是当前运单，使用完整的运单对象中的 ID
      let effectiveId = id;
      
      if (currentShipment) {
        console.log('当前运单信息:', {
          id: currentShipment.id,
          tracking: currentShipment.tracking || currentShipment.tracking_no
        });
        
        if (currentShipment.id === id) {
          effectiveId = currentShipment.id;
        }
      }
      
      // 确保 ID 是字符串类型
      effectiveId = String(effectiveId);
      console.log('使用有效的运单ID:', effectiveId);
      
      // 获取现有分享链接
      await fetchExistingLinks(effectiveId);
    } catch (error) {
      console.error('处理分享按钮错误:', error);
      message.error(error.message || '获取分享链接失败');
      setShareLink('');
      setShowCreateLinkForm(true);
    } finally {
      setShareLinkLoading(false);
    }
  };
  
  // 创建新的分享链接
  const createNewShareLink = async () => {
    if (!currentShipment || !currentShipment.id) {
      message.error('无法获取当前运单信息');
      return;
    }
    
    setShareLinkLoading(true);
    
    try {
      const effectiveId = String(currentShipment.id);
      console.log('创建新分享链接，运单ID:', effectiveId);
      
      // 计算过期时间（毫秒）
      const expirationMs = expirationDays * 24 * 60 * 60 * 1000;
      const expiresAt = Date.now() + expirationMs;
      
      // 创建新链接
      const result = await createShareableLink(effectiveId, { expiresAt });
      console.log('创建分享链接结果:', result);
      
      if (result.success) {
        const shareUrl = `${window.location.origin}/share/${result.linkId}`;
        console.log('生成分享 URL:', shareUrl);
        setShareLink(shareUrl);
        message.success('分享链接创建成功');
        
        // 重新获取链接列表
        await fetchExistingLinks(effectiveId);
        setShowCreateLinkForm(false);
      } else {
        console.error('创建分享链接失败:', result);
        message.error(result.message || '创建分享链接失败');
      }
    } catch (error) {
      console.error('创建分享链接错误:', error);
      message.error(error.message || '创建分享链接失败');
    } finally {
      setShareLinkLoading(false);
    }
  };
  
  // 复制分享链接
  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink)
      .then(() => {
        message.success('链接已复制到剪贴板');
      })
      .catch(() => {
        message.error('复制失败，请手动复制');
      });
  };
  
  // 表格列定义
  const columns = [
    {
      title: '快递单号',
      dataIndex: 'tracking',
      key: 'tracking',
      width: 150,
    },
    {
      title: '客户姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
    },
    {
      title: '货物内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      width: 110,
      render: (text) => formatDate(text),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        let color = 'default';
        let text = status || '待处理';
        
        if (status === 'pending' || status === '待处理') {
          color = 'default';
          text = '待处理';
        } else if (status === 'instock' || status === '已入库') {
          color = 'blue';
          text = '已入库';
        } else if (status === 'shipped' || status === '已出库') {
          color = 'orange';
          text = '已出库';
        } else if (status === 'delivered' || status === '已签收') {
          color = 'green';
          text = '已签收';
        } else if (status === 'completed' || status === '已完成') {
          color = 'green';
          text = '已完成';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => showDetail(record.id)}
          >
            详情
          </Button>
          <Button 
            type="default" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => navigate(`/edit/${record.id}`)}
          >
            编辑
          </Button>
          <Button 
            size="small" 
            icon={<ShareAltOutlined />}
            onClick={() => handleShare(record.id)}
          >
            分享
          </Button>
        </Space>
      ),
    },
  ];
  
  return (
    <div className="query-page">
      <Card>
        <Title level={4}>查询运单</Title>
        
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSearch}
          initialValues={{ status: '' }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="tracking" label="快递单号">
                <Input placeholder="输入快递单号" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="phone" label="联系电话">
                <Input placeholder="输入联系电话" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="name" label="客户姓名">
                <Input placeholder="输入客户姓名" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="dateRange" label="日期范围">
                <RangePicker 
                  style={{ width: '100%' }} 
                  locale={locale}
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="status" label="状态">
                <Select placeholder="选择状态">
                  <Option value="">全部</Option>
                  <Option value="已入库">已入库</Option>
                  <Option value="已出库">已出库</Option>
                  <Option value="运输中">运输中</Option>
                  <Option value="已签收">已签收</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              icon={<SearchOutlined />}
              loading={loading}
            >
              查询
            </Button>
          </Form.Item>
        </Form>
        
        <Spin spinning={loading}>
          <Table 
            columns={columns} 
            dataSource={shipments} 
            rowKey="id"
            pagination={pagination}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
          />
          
          {/* 调试信息显示 */}
          {debugInfo && (
            <div style={{ marginTop: 20, padding: 16, background: '#f0f0f0', borderRadius: 4 }}>
              <Typography.Title level={5}>调试信息</Typography.Title>
              <pre style={{ maxHeight: 300, overflow: 'auto' }}>
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}
        </Spin>
      </Card>
      
      {/* 运单详情抽屉 */}
      <Drawer
        title="运单详情"
        width={600}
        open={detailVisible}
        onClose={closeDetail}
        footer={
          <Space>
            <Button onClick={closeDetail}>关闭</Button>
            <Button 
              type="primary" 
              icon={<ShareAltOutlined />}
              onClick={() => currentShipment && handleShare(currentShipment.id)}
            >
              创建分享链接
            </Button>
          </Space>
        }
      >
        <Spin spinning={detailLoading}>
          {currentShipment && (
            <>
              <Descriptions bordered column={1}>
                <Descriptions.Item label="快递单号">{currentShipment.tracking || currentShipment.tracking_no || '无'}</Descriptions.Item>
                <Descriptions.Item label="客户姓名">{currentShipment.name || '无'}</Descriptions.Item>
                <Descriptions.Item label="联系电话">{currentShipment.phone || '无'}</Descriptions.Item>
                {currentShipment.address && (
                  <Descriptions.Item label="地址">{currentShipment.address}</Descriptions.Item>
                )}
                {currentShipment.content && (
                  <Descriptions.Item label="货物内容">{currentShipment.content}</Descriptions.Item>
                )}
                <Descriptions.Item label="日期">{formatDate(currentShipment.date) || '无日期'}</Descriptions.Item>
                {currentShipment.status && (
                  <Descriptions.Item label="状态">
                    <Tag color={
                      currentShipment.status === '已入库' ? 'blue' : 
                      currentShipment.status === '已出库' ? 'green' : 
                      currentShipment.status === '运输中' ? 'orange' : 
                      currentShipment.status === '已签收' ? 'green' : 'default'
                    }>
                      {currentShipment.status}
                    </Tag>
                  </Descriptions.Item>
                )}
                {currentShipment.weight && (
                  <Descriptions.Item label="重量">{currentShipment.weight} kg</Descriptions.Item>
                )}
                {currentShipment.quantity && (
                  <Descriptions.Item label="数量">{currentShipment.quantity}</Descriptions.Item>
                )}
                {currentShipment.cost && (
                  <Descriptions.Item label="费用">{currentShipment.cost} 元</Descriptions.Item>
                )}
                {currentShipment.notes && (
                  <Descriptions.Item label="备注">{currentShipment.notes}</Descriptions.Item>
                )}
                {currentShipment.created_at && (
                  <Descriptions.Item label="提交时间">{formatDateTime(currentShipment.created_at)}</Descriptions.Item>
                )}
                {currentShipment.operator && (
                  <Descriptions.Item label="操作人">{currentShipment.operator}</Descriptions.Item>
                )}
                {currentShipment.country && (
                  <Descriptions.Item label="国家/地区">{currentShipment.country}</Descriptions.Item>
                )}
              </Descriptions>
              
              {/* 显示运单图片 */}
              {currentShipment.images && currentShipment.images.length > 0 ? (
                <div style={{ marginTop: 20 }}>
                  <Title level={5}>运单图片</Title>
                  <Image.PreviewGroup>
                    <Space wrap>
                      {currentShipment.images.map((image, index) => (
                        <Image
                          key={index}
                          width={120}
                          src={fixImageUrl(image)}
                          alt={`图片 ${index + 1}`}
                          fallback="https://placehold.co/120x120?text=图片加载失败"
                        />
                      ))}
                    </Space>
                  </Image.PreviewGroup>
                </div>
              ) : currentShipment.image_urls ? (
                <div style={{ marginTop: 20 }}>
                  <Title level={5}>运单图片</Title>
                  <Image.PreviewGroup>
                    <Space wrap>
                      {/* 显示货物图片 */}
                      {currentShipment.image_urls.cargo && Array.isArray(currentShipment.image_urls.cargo) && 
                        currentShipment.image_urls.cargo.map((image, index) => (
                          <Image
                            key={`cargo-${index}`}
                            width={120}
                            src={fixImageUrl(image)}
                            alt={`货物图片 ${index + 1}`}
                            fallback="https://placehold.co/120x120?text=货物图片加载失败"
                          />
                        ))
                      }
                      
                      {/* 显示包装图片 */}
                      {currentShipment.image_urls.packing && Array.isArray(currentShipment.image_urls.packing) && 
                        currentShipment.image_urls.packing.map((image, index) => (
                          <Image
                            key={`packing-${index}`}
                            width={120}
                            src={fixImageUrl(image)}
                            alt={`包装图片 ${index + 1}`}
                            fallback="https://placehold.co/120x120?text=包装图片加载失败"
                          />
                        ))
                      }
                      
                      {/* 显示运输图片 */}
                      {currentShipment.image_urls.shipping && Array.isArray(currentShipment.image_urls.shipping) && 
                        currentShipment.image_urls.shipping.map((image, index) => (
                          <Image
                            key={`shipping-${index}`}
                            width={120}
                            src={fixImageUrl(image)}
                            alt={`运输图片 ${index + 1}`}
                            fallback="https://placehold.co/120x120?text=运输图片加载失败"
                          />
                        ))
                      }
                    </Space>
                  </Image.PreviewGroup>
                </div>
              ) : null}
            </>
          )}
        </Spin>
      </Drawer>
      
      {/* 分享链接模态框 */}
      <Modal
        title="分享运单"
        open={shareModalVisible}
        onCancel={() => setShareModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setShareModalVisible(false)}>
            关闭
          </Button>,
          shareLink && (
            <Button 
              key="copy" 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={copyShareLink}
            >
              复制链接
            </Button>
          )
        ]}
      >
        <Spin spinning={shareLinkLoading}>
          {existingLinks.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>现有分享链接</Title>
              {existingLinks.map((link, index) => (
                <div key={link.id} style={{ marginBottom: 8, padding: 8, border: '1px solid #f0f0f0', borderRadius: 4 }}>
                  <Row gutter={[8, 8]} align="middle">
                    <Col span={16}>
                      <Text 
                        ellipsis 
                        style={{ width: '100%' }} 
                        type={link.isExpired ? 'secondary' : undefined}
                        delete={link.isExpired}
                      >
                        {`${window.location.origin}/share/${link.id}`}
                      </Text>
                    </Col>
                    <Col span={8} style={{ textAlign: 'right' }}>
                      <Space>
                        <Tag color={link.isExpired ? 'default' : 'green'}>
                          {link.isExpired ? '已过期' : '有效'}
                        </Tag>
                        <Button 
                          size="small" 
                          type="link" 
                          onClick={() => {
                            const url = `${window.location.origin}/share/${link.id}`;
                            setShareLink(url);
                            navigator.clipboard.writeText(url);
                            message.success('链接已复制');
                          }}
                          disabled={link.isExpired}
                        >
                          复制
                        </Button>
                        <Button 
                          size="small" 
                          type="link" 
                          danger
                          onClick={() => handleDeleteLink(link.id)}
                        >
                          删除
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                    <Space split={<span style={{ margin: '0 8px' }}>|</span>}>
                      <span>创建于: {formatDateTime(link.createdAt)}</span>
                      {link.expiresAt && (
                        <span>过期于: {formatDateTime(link.expiresAt)}</span>
                      )}
                      <span>访问次数: {link.accessCount}</span>
                    </Space>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {showCreateLinkForm ? (
            <div>
              <Title level={5}>创建新的分享链接</Title>
              <Form layout="vertical">
                <Form.Item label="有效期">
                  <Select 
                    value={expirationDays} 
                    onChange={setExpirationDays}
                    style={{ width: '100%' }}
                  >
                    <Option value={1}>1天</Option>
                    <Option value={3}>3天</Option>
                    <Option value={7}>7天</Option>
                    <Option value={30}>30天</Option>
                    <Option value={90}>90天</Option>
                  </Select>
                </Form.Item>
                <Button 
                  type="primary" 
                  onClick={createNewShareLink} 
                  loading={shareLinkLoading}
                  block
                >
                  生成分享链接
                </Button>
              </Form>
            </div>
          ) : (
            !shareLinkLoading && existingLinks.length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Paragraph>暂无分享链接</Paragraph>
                <Button 
                  type="primary" 
                  onClick={() => setShowCreateLinkForm(true)}
                >
                  创建分享链接
                </Button>
              </div>
            )
          )}
          
          {shareLink && !showCreateLinkForm && (
            <div style={{ marginTop: 16 }}>
              <Paragraph>
                当前活跃链接：
              </Paragraph>
              <Paragraph copyable={{ text: shareLink }}>
                <Text ellipsis style={{ width: '100%' }}>{shareLink}</Text>
              </Paragraph>
              <Paragraph type="secondary">
                注意：此链接可以被任何人访问，请谨慎分享。
              </Paragraph>
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Button 
                  type="default" 
                  onClick={() => setShowCreateLinkForm(true)}
                >
                  创建新链接
                </Button>
              </div>
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default Query;
